/**
 * TanStack Query hooks for the tracker feature.
 */

import { useEffect, useRef } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { getStoredToken } from '@/features/auth/utils/authStorage';

import {
  clearSourceCache,
  createDataset,
  createSource,
  deleteDataset,
  deleteSource,
  getBoardScaffold,
  getCapabilities,
  getDataset,
  getIngestion,
  getLatestIngestion,
  getLatestTeamRefresh,
  getTeamThroughput,
  getSourceFields,
  getTeamTrackerState,
  listDatasets,
  listSourceBoards,
  listSourceLinkTypes,
  listSources,
  previewDataset,
  previewSourceQuery,
  setTeamDataset,
  setTeamThroughputSettings,
  startTeamRefresh,
  triggerIngest,
  updateDataset,
  updateSource,
} from './api';
import type { ApplyPart, TeamRefreshStatus, ThroughputSettingsBody } from './types';

export const trackerKeys = {
  sources: ['tracker', 'sources'] as const,
  datasets: ['tracker', 'datasets'] as const,
  dataset: (id: string) => ['tracker', 'datasets', id] as const,
  latestIngestion: (datasetId: string) => ['tracker', 'datasets', datasetId, 'latest-ingestion'] as const,
  ingestion: (id: string) => ['tracker', 'ingestions', id] as const,
  teamState: (teamId: string) => ['tracker', 'simulation-teams', teamId] as const,
  // Nested under `teamState`: a data-source pick invalidates that prefix, and
  // with it the team's refreshes and throughput — both follow the dataset.
  teamRefresh: (teamId: string, part: ApplyPart) => ['tracker', 'simulation-teams', teamId, 'refreshes', part] as const,
  teamThroughput: (teamId: string) => ['tracker', 'simulation-teams', teamId, 'throughput'] as const,
  capabilities: ['tracker', 'capabilities'] as const,
  sourceFields: (sourceId: string) => ['tracker', 'source-fields', sourceId] as const,
  sourceBoards: (sourceId: string) => ['tracker', 'source-boards', sourceId] as const,
  sourceLinkTypes: (sourceId: string) => ['tracker', 'source-link-types', sourceId] as const,
} as const;

// --- Capabilities ------------------------------------------------------------

/**
 * Tracker entitlement + configuration state, used to gate the UI. Only runs
 * for authenticated users (the endpoint requires auth). Cached for a minute —
 * licensing/config changes are rare and a stale read merely delays a banner.
 */
export function useTrackerCapabilities() {
  return useQuery({
    queryKey: trackerKeys.capabilities,
    queryFn: getCapabilities,
    staleTime: 60_000,
    enabled: !!getStoredToken(),
  });
}

// --- Sources -----------------------------------------------------------------

/**
 * The tracker sources. Admin-only on the server (`list_sources`), so a
 * non-admin caller passes `enabled: false` rather than issuing a request
 * that can only be refused.
 */
export function useSources({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: trackerKeys.sources,
    queryFn: listSources,
    enabled,
  });
}

export function useCreateSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createSource,
    onSuccess: () => qc.invalidateQueries({ queryKey: trackerKeys.sources }),
  });
}

export function useUpdateSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...req }: Parameters<typeof updateSource>[1] & { id: string }) => updateSource(id, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: trackerKeys.sources }),
  });
}

export function useDeleteSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteSource,
    onSuccess: () => qc.invalidateQueries({ queryKey: trackerKeys.sources }),
  });
}

/**
 * Clear a tracker source's cached work items.
 *
 * Invalidates the dataset queries, not the source ones: the source row itself
 * is unchanged, while every dataset on that tracker host now faces a full
 * refetch on its next ingest.
 */
export function useClearSourceCache() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: clearSourceCache,
    onSuccess: () => qc.invalidateQueries({ queryKey: trackerKeys.datasets }),
  });
}

// --- Datasets ----------------------------------------------------------------

export function useDatasets() {
  return useQuery({
    queryKey: trackerKeys.datasets,
    queryFn: listDatasets,
  });
}

/**
 * A query that is waiting for its id is disabled, and a disabled query still
 * needs a key — one of its OWN. These hooks used to borrow the datasets-list
 * key for that. React Query keeps one options object per key, written by
 * whichever observer rendered last, so a form holding
 * `useLatestIngestion(undefined)` handed the list query
 * `getLatestIngestion(undefined)` as its fetcher; the invalidation after
 * "Create" then refetched the list from `/datasets/undefined/ingestions/latest`
 * and rendered an empty card. The id-shaped key with an empty id is under no
 * other query's prefix and is never fetched, because `enabled` is false.
 */
const NO_ID = '';

export function useDataset(id: string | undefined) {
  return useQuery({
    queryKey: trackerKeys.dataset(id ?? NO_ID),
    queryFn: () => getDataset(id!),
    enabled: !!id,
  });
}

export function useCreateDataset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createDataset,
    onSuccess: () => qc.invalidateQueries({ queryKey: trackerKeys.datasets }),
  });
}

export function useUpdateDataset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...req }: Parameters<typeof updateDataset>[1] & { id: string }) => updateDataset(id, req),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: trackerKeys.datasets });
      qc.invalidateQueries({ queryKey: trackerKeys.dataset(vars.id) });
    },
  });
}

export function useDeleteDataset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteDataset,
    onSuccess: () => qc.invalidateQueries({ queryKey: trackerKeys.datasets }),
  });
}

// --- Ingestions --------------------------------------------------------------

export function useLatestIngestion(datasetId: string | undefined, refetchInterval?: number | false) {
  return useQuery({
    queryKey: trackerKeys.latestIngestion(datasetId ?? NO_ID),
    queryFn: () => getLatestIngestion(datasetId!),
    enabled: !!datasetId,
    refetchInterval: refetchInterval ?? false,
  });
}

export function useIngestion(id: string | undefined, refetchInterval?: number | false) {
  return useQuery({
    queryKey: trackerKeys.ingestion(id ?? NO_ID),
    queryFn: () => getIngestion(id!),
    enabled: !!id,
    refetchInterval: refetchInterval ?? false,
  });
}

export function useTriggerIngest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: triggerIngest,
    onSuccess: (ingestion) => {
      qc.invalidateQueries({ queryKey: trackerKeys.latestIngestion(ingestion.dataset_id) });
    },
  });
}

// --- Simulation-team tracker state ------------------------------------------

export function useTeamTrackerState(simulationTeamId: string | undefined) {
  return useQuery({
    queryKey: trackerKeys.teamState(simulationTeamId ?? NO_ID),
    queryFn: () => getTeamTrackerState(simulationTeamId!),
    enabled: !!simulationTeamId,
  });
}

/**
 * The tracker state of several teams at once — the siblings of the team a
 * card is attached to, so it can say BEFORE Apply that one of them already
 * uses the dataset being applied.
 *
 * Same key per team as {@link useTeamTrackerState}: the card's own team and
 * its siblings' cards then share one cache entry per team, and the
 * invalidation `useSetTeamDataset` issues on a pick reaches every card that
 * shows that team. A separate "all teams in a simulation" endpoint would
 * need its own invalidation wiring for what is, per simulation, a handful of
 * small reads.
 */
export function useTeamTrackerStates(simulationTeamIds: readonly string[]) {
  return useQueries({
    queries: simulationTeamIds.map((id) => ({
      queryKey: trackerKeys.teamState(id),
      queryFn: () => getTeamTrackerState(id),
    })),
  });
}

export function useSetTeamDataset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ simulationTeamId, datasetId }: { simulationTeamId: string; datasetId: string | null }) =>
      setTeamDataset(simulationTeamId, datasetId),
    onSuccess: (state) => {
      qc.invalidateQueries({ queryKey: trackerKeys.teamState(state.simulation_team_id) });
    },
  });
}

// --- A team's own refreshes (shipcast-o7j.3/.5) ----------------------------

/** How often a running refresh is re-read. */
export const TEAM_REFRESH_POLL_MS = 2000;

/**
 * The team's latest refresh of `part`, polled while it runs.
 *
 * `onOk` fires when an observed refresh BECOMES `ok` — a running one
 * finishing, or a new one found already finished — but not on the first
 * observation: opening a page onto an old finished refresh applied nothing
 * just now. The server applied the data, so the caller reloads what shows it
 * (the team form holds its own copy of the deliverables and throughput).
 */
export function useLatestTeamRefresh(
  simulationTeamId: string | undefined,
  part: ApplyPart,
  { onOk }: { onOk?: () => void } = {},
) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: trackerKeys.teamRefresh(simulationTeamId ?? NO_ID, part),
    queryFn: () => getLatestTeamRefresh(simulationTeamId!, part),
    enabled: !!simulationTeamId,
    refetchInterval: (q) => (q.state.data?.status === 'running' ? TEAM_REFRESH_POLL_MS : false),
  });

  // `undefined` = nothing observed yet; `null` = observed "never refreshed".
  const prevRef = useRef<{ id: string; status: TeamRefreshStatus } | null | undefined>(undefined);
  const onOkRef = useRef(onOk);
  useEffect(() => {
    onOkRef.current = onOk;
  });
  const refresh = query.data;
  useEffect(() => {
    if (refresh === undefined) return;
    const prev = prevRef.current;
    prevRef.current = refresh ? { id: refresh.id, status: refresh.status } : null;
    if (!refresh || prev === undefined) return;
    const changed = !prev || prev.id !== refresh.id || prev.status !== refresh.status;
    if (changed && refresh.status === 'ok') {
      // The refresh fetched a new ingest: the team's throughput history (and
      // tracker state) moved with it, whichever part was applied.
      void qc.invalidateQueries({ queryKey: trackerKeys.teamState(refresh.simulation_team_id) });
      onOkRef.current?.();
    }
  }, [refresh, qc]);

  return query;
}

/**
 * Start a refresh of one part. The `running` row it returns is written
 * straight into the latest-refresh cache, which is what starts the poll.
 */
export function useStartTeamRefresh() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ simulationTeamId, part }: { simulationTeamId: string; part: ApplyPart }) =>
      startTeamRefresh(simulationTeamId, part),
    onSuccess: (refresh) => {
      qc.setQueryData(trackerKeys.teamRefresh(refresh.simulation_team_id, refresh.part), refresh);
    },
  });
}

// --- A team's throughput (shipcast-o7j.2/.6/.7) ------------------------------

export function useTeamThroughput(simulationTeamId: string | undefined) {
  return useQuery({
    queryKey: trackerKeys.teamThroughput(simulationTeamId ?? NO_ID),
    queryFn: () => getTeamThroughput(simulationTeamId!),
    enabled: !!simulationTeamId,
  });
}

/**
 * Where a team's throughput comes from, for its row (shipcast-o7j.6): from
 * its dataset — then the row shows charts, not a text box a refresh would
 * overwrite — or typed by hand. `lastDay` dates the series: a tracker cut
 * ends on its range's last day.
 */
export function useTeamThroughputSource(simulationTeamId: string | undefined): {
  fromDataset: boolean;
  lastDay: string | null;
} {
  const { data: capabilities } = useTrackerCapabilities();
  const available = !!capabilities?.licensed && !!capabilities.configured;
  const { data: state } = useTeamTrackerState(available ? simulationTeamId : undefined);
  const fromDataset = !!state?.dataset_id;
  const { data: throughput } = useTeamThroughput(fromDataset ? simulationTeamId : undefined);
  return { fromDataset, lastDay: throughput?.history?.range_to ?? null };
}

export function useSetTeamThroughputSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ simulationTeamId, settings }: { simulationTeamId: string; settings: ThroughputSettingsBody }) =>
      setTeamThroughputSettings(simulationTeamId, settings),
    onSuccess: (throughput) => {
      qc.setQueryData(trackerKeys.teamThroughput(throughput.simulation_team_id), throughput);
    },
  });
}

/**
 * Discovered fields for one source, `enabled` only once a source is chosen.
 * Cached per source for a few minutes — the catalogue changes when an admin
 * adds a field, not per keystroke — and retried once, since a failing probe
 * is usually a real config problem the caller should surface, not a blip.
 */
export function useSourceFields(sourceId: string | null) {
  return useQuery({
    queryKey: trackerKeys.sourceFields(sourceId ?? ''),
    queryFn: () => getSourceFields(sourceId!),
    enabled: !!sourceId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/**
 * Boards for one source, on the same terms as {@link useSourceFields}: a
 * live probe, enabled only once a source is chosen, cached for a few minutes
 * because a board list changes when someone creates a board, not per
 * keystroke.
 *
 * Callers must treat both an error and an empty list as "offer manual
 * setup", never as a blocker — a source can legitimately have no boards.
 */
export function useSourceBoards(sourceId: string | null) {
  return useQuery({
    queryKey: trackerKeys.sourceBoards(sourceId ?? ''),
    queryFn: () => listSourceBoards(sourceId!),
    enabled: !!sourceId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/**
 * Link types for one source, on the same terms as {@link useSourceBoards}: a
 * live probe, enabled only once a source is chosen, cached for minutes
 * because the vocabulary changes when an admin adds a link type, not per
 * keystroke.
 *
 * Callers must treat an error and an empty list identically — fall back to
 * free-text entry. Both mean "we do not know this tracker's vocabulary", and
 * a picker offering nothing would block a field that must stay editable.
 */
export function useSourceLinkTypes(sourceId: string | null) {
  return useQuery({
    queryKey: trackerKeys.sourceLinkTypes(sourceId ?? ''),
    queryFn: () => listSourceLinkTypes(sourceId!),
    enabled: !!sourceId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/**
 * Fetch one board's scaffold on demand.
 *
 * A mutation rather than a query even though the endpoint is a GET: this is
 * an imperative action the user takes once ("use this board"), whose result
 * is copied into form state and then owned by the form. Modelling it as a
 * query would leave a cache entry that could later re-render and silently
 * overwrite edits the user made after the prefill.
 */
export function useBoardScaffold() {
  return useMutation({
    mutationFn: ({ sourceId, boardId }: { sourceId: string; boardId: string }) => getBoardScaffold(sourceId, boardId),
  });
}

/**
 * Preview one query on demand.
 *
 * A mutation rather than a query because it is user-triggered and must not
 * be cached or refetched on focus: every run is a full key listing against
 * the tracker, and the answer is only meaningful for the exact query text
 * that was in the box when the button was pressed.
 */
export function useQueryPreview() {
  return useMutation({
    mutationFn: ({ sourceId, query }: { sourceId: string; query: string }) => previewSourceQuery(sourceId, query),
  });
}

/**
 * Dry-run the ingest for the dataset as it stands in the form.
 *
 * A mutation for the same reasons as `useQueryPreview`: user-triggered, a
 * full fetch against the tracker, and meaningful only for the exact config
 * that was in the form when the button was pressed — never cached, never
 * refetched on focus.
 */
export function useDatasetPreview() {
  return useMutation({
    mutationFn: (body: Parameters<typeof previewDataset>[0]) => previewDataset(body),
  });
}
